import React, { useState, useMemo, useEffect, useCallback, useRef, useDeferredValue } from 'react';
import {
    Box, Typography, Grid, Button, Stack, Chip, Avatar, IconButton, Tooltip, InputBase, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, InputAdornment, Snackbar, Alert,
    Checkbox, FormControlLabel, Menu, ListItemIcon, Popover,
} from '@mui/material';
import * as XLSX from 'xlsx';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import { useSelector } from 'react-redux';
import { selectActiveEntityId } from '../../redux/slices/orgSlice';
import { readVacancyCategories } from './CategoriesTab';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import MailRoundedIcon from '@mui/icons-material/MailRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import CallRoundedIcon from '@mui/icons-material/CallRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import WorkHistoryRoundedIcon from '@mui/icons-material/WorkHistoryRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import { useNavigate } from 'react-router-dom';
import SearchSelect from '../SearchSelect';
import { initialsFromName as initials, avatarTone } from '../../utils/format';
import { solidBtn, ghostBtn, tonalBtn, field, th, td, Panel, EmptyState, ConfirmDialog, SkeletonRows, LoadAlert } from '../uiKit';
import http, { apiErrorMessage } from '../../Api/http';
import {
    GetCandidates, GetInterviews, SetCandidateStage, AddCandidate, UpdateCandidate, DeleteCandidate, GetVacancies,
    AddCandidateCommunication, UpdateCandidateCommunication, DeleteCandidateCommunication,
    AddCandidateEmployment, UpdateCandidateEmployment, DeleteCandidateEmployment, UploadCandidateResume,
    GetCandidateCommunications, GetCandidateEmployments,
} from '../../Api/Api';

// apiErrorMessage only reads axios errors; the `throw new Error(body.message)` paths
// carry the server's text on err.message, which would otherwise fall back to a generic line.
const errText = (err, fallback) => (err?.response || err?.request || err?.code ? apiErrorMessage(err, fallback) : (err?.message || fallback));

// Single-candidate log endpoints return { error, message, data } where the rows sit at data,
// data.<key> or data.items. A 404 means the candidate has no rows yet.
const pickRows = (body, key) => {
    const d = body?.data;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.[key])) return d[key];
    if (Array.isArray(d?.items)) return d.items;
    return [];
};
const fetchCandidateRows = async (url, candidateId, key) => {
    try {
        const { data: body } = await http.get(url, { params: { candidateId } });
        if (body?.error) throw new Error(body.message || 'Request failed.');
        return pickRows(body, key);
    } catch (err) {
        if (err?.response?.status === 404) return [];
        throw err;
    }
};

export const SOURCES = ['Meta Ads', 'Referral', 'LinkedIn', 'Naukri', 'Walk-in', 'Career Page', 'Agency', 'Other'];

export const STAGES = {
    applied: { label: 'Applied', color: '#64748B', bg: '#F1F5F9' },
    interviewing: { label: 'Interviewing', color: '#0369A1', bg: '#E0F2FE' },
    'on-hold': { label: 'On Hold', color: '#B45309', bg: '#FFF7ED' },
    selected: { label: 'Selected', color: '#16A34A', bg: '#DCFCE7' },
    joined: { label: 'Joined', color: '#15803D', bg: '#DCFCE7' },
    rejected: { label: 'Rejected', color: '#E11D48', bg: '#FEE2E2' },
};
const stageKey = (s) => {
    const t = String(s || '').toLowerCase().replace(/\s+/g, '-');
    return STAGES[t] ? t : 'applied';
};

const EMPTY = {
    vacancyId: '', name: '', email: '', phone: '', experience: '', currentCompany: '', city: '',
    currentSalary: '', expectedSalary: '', noticePeriod: '30 days', source: 'Referral', communications: [], employments: [], resume: null,
};

const FOLLOW_UP_STATUS = 'Follow-up Scheduled';
const MAX_COMM_ROWS = 10;
const RESUME_TYPES = ['.pdf', '.doc', '.docx'];
const MAX_RESUME_BYTES = 10 * 1024 * 1024;
const fmtSize = (b) => (b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const MAX_EMP_ROWS = 10;

// The Interviews module is the source of truth for where a candidate is in the process,
// so the stage shown here follows their rounds: a round still to happen means Interviewing,
// otherwise the latest decided round's outcome. Joined/Rejected are final and stay as the server has them.
const OUTCOME_STAGE = { Selected: 'selected', 'On Hold': 'on-hold', Rejected: 'rejected' };
function withInterviewStage(candidates, interviews) {
    const byCandidate = new Map();
    interviews.forEach((iv) => {
        if (iv.status === 'Cancelled' || iv.candidateId == null) return;
        const k = String(iv.candidateId);
        byCandidate.set(k, [...(byCandidate.get(k) || []), iv]);
    });
    return candidates.map((c) => {
        const ivs = byCandidate.get(String(c.id));
        if (!ivs || c.stage === 'joined' || c.stage === 'rejected') return c;
        const byNewest = [...ivs].sort((a, b) => Number(b.id) - Number(a.id));
        const pending = byNewest.find((iv) => !iv.outcome && iv.status !== 'No Show');
        if (pending) return { ...c, stage: 'interviewing', stageRound: pending.round };
        const decided = byNewest.find((iv) => OUTCOME_STAGE[iv.outcome]);
        if (decided) return { ...c, stage: OUTCOME_STAGE[decided.outcome], stageRound: decided.round };
        return c;
    });
}

// Previous-employer rows. Months are <input type="month"> values (yyyy-mm).
const emptyEmpRow = () => ({ id: null, company: '', designation: '', fromMonth: '', toMonth: '', current: false, lastSalary: '', reasonForLeaving: '' });
const isYM = (v) => /^\d{4}-\d{2}$/.test(v || '');
const empRowFilled = (r) => Boolean(r.company.trim() || r.designation.trim() || r.fromMonth || r.toMonth || r.lastSalary || r.reasonForLeaving.trim());
const empRowValid = (r) => r.company.trim() && r.designation.trim() && isYM(r.fromMonth) && (r.current || (isYM(r.toMonth) && r.toMonth >= r.fromMonth && r.reasonForLeaving.trim()));
const monthIndex = (ym) => { const [y, m] = String(ym).split('-').map(Number); return y * 12 + (m - 1); };
const nowMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
const empMonths = (r) => (isYM(r.fromMonth) ? Math.max(0, monthIndex(r.current ? nowMonth() : (isYM(r.toMonth) ? r.toMonth : r.fromMonth)) - monthIndex(r.fromMonth) + 1) : 0);
const durationText = (months) => {
    if (!months) return '—';
    const y = Math.floor(months / 12); const m = months % 12;
    return [y ? `${y} yr${y === 1 ? '' : 's'}` : '', m ? `${m} mo` : ''].filter(Boolean).join(' ');
};

const normalizeCommunication = (c) => ({
    id: c.id,
    candidateId: c.candidateId,
    communicationType: c.communicationType ?? '',
    status: c.status ?? '',
    description: c.description ?? '',
    communicatedOn: c.communicatedOn ?? '',
    followUpDate: c.followUpDate ?? null,
    loggedBy: c.loggedBy ?? '',
    loggedOn: c.loggedOn ?? '',
});

// Lead answers arrive lowercase ("kumbakonam"); show them capitalized.
export const titleCase = (v) => String(v ?? '').replace(/(^|[\s/(-])([a-z])/g, (_, a, b) => a + b.toUpperCase());

const leadAnswer = (answers, re) => (Array.isArray(answers) ? answers : []).find((a) => re.test(a?.question || ''))?.answer ?? '';

// "13 Sep 2026" -> "2026-09-13" for the applied-date filter.
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const appliedISO = (s) => {
    const m = /(\d{1,2})\s+([A-Za-z]{3})\w*\s+(\d{4})/.exec(s || '');
    const i = m ? MONTHS.indexOf(m[2].toLowerCase()) : -1;
    return i < 0 ? '' : `${m[3]}-${String(i + 1).padStart(2, '0')}-${m[1].padStart(2, '0')}`;
};
const isoOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const dateRangeFor = (mode, from, to) => {
    const now = new Date();
    if (mode === 'today') return [isoOf(now), isoOf(now)];
    if (mode === 'this') return [isoOf(new Date(now.getFullYear(), now.getMonth(), 1)), isoOf(new Date(now.getFullYear(), now.getMonth() + 1, 0))];
    if (mode === 'last') return [isoOf(new Date(now.getFullYear(), now.getMonth() - 1, 1)), isoOf(new Date(now.getFullYear(), now.getMonth(), 0))];
    if (mode === 'custom') return [from, to];
    return ['', ''];
};

export const normalizeCandidate = (c) => ({
    id: c.id,
    vacancyId: c.vacancyId,
    vacancyTitle: c.vacancyTitle ?? '',
    name: c.fullName ?? '',
    // An import with no email saved a stand-in address; show it as empty.
    email: /@noemail\.local$/i.test(c.email ?? '') ? '' : (c.email ?? ''),
    trackingCode: c.trackingCode ?? '',
    phone: c.phone ?? '',
    currentCompany: c.currentCompany ?? '',
    isFresher: Boolean(c.isFresher),
    experienceYears: Number(c.experienceYears) || 0,
    leadExperience: (Array.isArray(c.leadAnswers) ? c.leadAnswers : []).find((a) => /experience/i.test(a?.question || ''))?.answer ?? '',
    leadAnswers: Array.isArray(c.leadAnswers) ? c.leadAnswers : [],
    // City: the careers-form answer, else a city / location the server returns, else the location an import noted in the communication log.
    city: leadAnswer(c.leadAnswers, /^city/i) || c.city || c.location || ((Array.isArray(c.communications) ? c.communications : []).map((m) => /Location:\s*([^·]+)/i.exec(m?.description || '')?.[1]?.trim()).find(Boolean) || ''),
    locationInterest: leadAnswer(c.leadAnswers, /location/i),
    metaCampaignName: c.metaCampaignName ?? '',
    metaAdName: c.metaAdName ?? '',
    metaPlatform: c.metaPlatform ?? '',
    joinedOn: c.joinedOn ?? '',
    entityName: c.entityName ?? '',
    // An import once saved "20K" as 20; no real expected salary is under 1,000 rupees, so read such a value as thousands.
    expectedSalary: (() => { const n = Number(c.expectedSalary) || 0; return n > 0 && n < 1000 ? n * 1000 : n; })(),
    noticePeriod: c.noticePeriod ?? '',
    source: c.source ?? '',
    stage: stageKey(c.stage),
    // A candidate brought in from a sheet carries the stand-in address and no real applied date (the server stamps the import day), so it shows blank.
    appliedOn: /@noemail\.local$/i.test(c.email ?? '') ? '' : (c.appliedOn ?? ''),
    rejectReason: c.rejectReason ?? null,
    roundsDecided: Number(c.roundsDecided) || 0,
    roundsTotal: Number(c.roundsTotal) || 0,
    communications: Array.isArray(c.communications) ? c.communications.map(normalizeCommunication) : [],
    employments: Array.isArray(c.employments) ? c.employments.map(normalizeEmployment) : [],
    totalEmploymentDuration: durationLabel(c.totalEmploymentDuration),
    resumeFileName: c.resumeFileName ?? '',
    resumePath: c.resumePath ?? '',
    resumeUrl: c.resumeUrl ?? '',
});

// totalEmploymentDuration comes back as a summary of the candidate's previous jobs; its exact
// shape isn't documented, so a plain string, a { label/text } object or years+months all read.
const durationLabel = (v) => {
    if (!v) return '';
    if (typeof v === 'string') return v;
    const text = v.label ?? v.text ?? v.display ?? v.duration;
    if (text) return String(text);
    const y = Number(v.years ?? v.totalYears ?? 0); const m = Number(v.months ?? v.totalMonths ?? 0);
    return [y ? `${y} yr${y === 1 ? '' : 's'}` : '', m ? `${m} mo` : ''].filter(Boolean).join(' ');
};

// Meta leads flag isFresher=true even when they answer "3–5 years", so experienceYears / the lead answer win.
const expText = (c) => {
    if (c.experienceYears > 0) return `${c.experienceYears} yr${c.experienceYears === 1 ? '' : 's'}`;
    if (c.leadExperience && !/fresher/i.test(c.leadExperience)) return c.leadExperience;
    return 'Fresher';
};

// dd-MM-yyyy the API wants, from a <input type="date"> value (yyyy-mm-dd).
const toDMY = (iso) => {
    if (!iso) return '';
    const [y, m, d] = String(iso).slice(0, 10).split('-');
    return d ? `${d}-${m}-${y}` : '';
};
// The log renders dates pretty ("15 Sep 2026"); parse that back to yyyy-mm-dd
// to prefill a date input when editing an entry.
const prettyToISO = (s) => {
    if (!s) return '';
    const t = new Date(s);
    if (Number.isNaN(t.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`;
};

const emptyCommRow = () => ({ id: null, editing: true, communicationType: '', status: '', description: '', communicatedOn: '', followUpDate: '' });
// A communication already on the server, shown collapsed with an edit icon until opened.
const toCommRow = (c) => ({
    id: c.id,
    editing: false,
    communicationType: c.communicationType || '',
    status: c.status || '',
    description: c.description || '',
    communicatedOn: prettyToISO(c.communicatedOn),
    followUpDate: prettyToISO(c.followUpDate),
    savedDate: c.communicatedOn || '',
    savedFollowUp: c.followUpDate || '',
    loggedBy: c.loggedBy || '',
});
const commRowActive = (row) => row.id == null || row.editing;
const commRowFilled = (row) => Boolean(row.communicationType || row.status || row.description.trim());
const commRowValid = (row) => row.communicationType && row.status && (row.status !== FOLLOW_UP_STATUS || row.followUpDate);
const commPayload = (row) => {
    const out = {
        communicationType: row.communicationType,
        status: row.status,
        description: row.description.trim(),
    };
    if (row.communicatedOn) out.communicatedOn = toDMY(row.communicatedOn);
    if (row.status === FOLLOW_UP_STATUS && row.followUpDate) out.followUpDate = toDMY(row.followUpDate);
    return out;
};

// 40px fields with the text centred vertically, so the inputs in an experience
// card line up with the Month / Year dropdowns beside them.
const empField = {
    ...field,
    '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 40 },
    '& .MuiOutlinedInput-input': { py: 0, height: '100%', boxSizing: 'border-box' },
    '& .MuiInputLabel-root': { fontSize: 14 },
};

// Main dialog fields: labels always sit on the border (so empty and filled fields
// look alike) and match the notch size, which stops the border cutting through them.
const dlgField = { ...field, '& .MuiInputLabel-root': { fontSize: 14 } };

// The API speaks "MM-yyyy"; the form works in "yyyy-mm" (what the Month/Year pickers emit).
const toMY = (ym) => (isYM(ym) ? `${ym.slice(5, 7)}-${ym.slice(0, 4)}` : '');
const fromMY = (v) => {
    const t = String(v || '').trim();
    let m = t.match(/^(\d{1,2})-(\d{4})$/);
    if (m) return `${m[2]}-${m[1].padStart(2, '0')}`;
    m = t.match(/^(\d{1,2})-(\d{1,2})-(\d{4})/);
    if (m) return `${m[3]}-${m[2].padStart(2, '0')}`;
    m = t.match(/^(\d{4})-(\d{2})/);
    if (m) return `${m[1]}-${m[2]}`;
    const d = new Date(t);
    return Number.isNaN(d.getTime()) ? '' : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const normalizeEmployment = (e) => ({
    id: e.id ?? null,
    company: e.companyName ?? '',
    designation: e.designation ?? '',
    fromMonth: fromMY(e.fromDate),
    toMonth: e.isCurrentlyWorking ? '' : fromMY(e.toDate),
    current: Boolean(e.isCurrentlyWorking),
    lastSalary: e.lastSalary ? String(Math.round(Number(e.lastSalary))) : '',
    reasonForLeaving: e.reasonForLeaving ?? '',
});
const empPayload = (r) => ({
    companyName: r.company.trim(),
    designation: r.designation.trim(),
    fromDate: toMY(r.fromMonth),
    toDate: r.current ? '' : toMY(r.toMonth),
    isCurrentlyWorking: r.current,
    lastSalary: Number(r.lastSalary) || 0,
    reasonForLeaving: r.current ? '' : r.reasonForLeaving.trim(),
});

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const YEAR_OPTIONS = Array.from({ length: 51 }, (_, i) => new Date().getFullYear() - i);

// A month + year pair of dropdowns in place of <input type="month">, which renders
// as an unreadable "----------, ----" mask in most browsers. Value is "yyyy-mm".
function MonthYearField({ label, value, onChange, error }) {
    const [y = '', m = ''] = value ? value.split('-') : [];
    const emit = (nextY, nextM) => onChange(nextY || nextM ? `${nextY}-${nextM}` : '');
    return (
        <Box>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: error ? '#E11D48' : '#64748B', mb: 0.6 }}>{label}</Typography>
            <Stack direction="row" spacing={1}>
                <TextField select size="small" value={m} onChange={(e) => emit(y, e.target.value)} error={error} sx={{ ...field, flex: 1.2 }}
                    slotProps={{ select: { displayEmpty: true, renderValue: (v) => (v ? MONTH_NAMES[Number(v) - 1] : <Box component="span" sx={{ color: '#94A3B8' }}>Month</Box>) } }}>
                    {MONTH_NAMES.map((n, i) => <MenuItem key={n} value={String(i + 1).padStart(2, '0')} sx={{ fontSize: 13 }}>{n}</MenuItem>)}
                </TextField>
                <TextField select size="small" value={y} onChange={(e) => emit(e.target.value, m)} error={error} sx={{ ...field, flex: 1 }}
                    slotProps={{ select: { displayEmpty: true, renderValue: (v) => v || <Box component="span" sx={{ color: '#94A3B8' }}>Year</Box>, MenuProps: { slotProps: { paper: { sx: { maxHeight: 280 } } } } } }}>
                    {YEAR_OPTIONS.map((yr) => <MenuItem key={yr} value={String(yr)} sx={{ fontSize: 13 }}>{yr}</MenuItem>)}
                </TextField>
            </Stack>
        </Box>
    );
}

// Fixed column widths so the extra table width is not spread between neighbouring columns.
// Fixed-width columns: long unbroken values (e.g. "Srinivasapuram,Thanjavur") wrap inside their cell instead of spilling into the next one.
// break-word only splits a word that cannot fit on a line by itself, so normal words stay whole.
const cell = { ...td, overflowWrap: 'break-word', verticalAlign: 'middle' };
// Adds a space after ":" "/" "," so values like "Native:papanasam/Worklocation:Thanjavur" wrap at those points.
const spaced = (v) => String(v || '').replace(/([:/,])(?=\S)/g, '$1 ');
const COL_W = { CANDIDATE: 230, 'APPLIED FOR': 200, 'APPLIED DATE': 130, SOURCE: 120, LOCATION: 190, EXPERIENCE: 120, STAGE: 150, ACTIONS: 90 };

const FILTER_KEY = 'candidates.filters';
const readSavedFilters = () => {
    try { return JSON.parse(sessionStorage.getItem(FILTER_KEY) || '{}') || {}; } catch { return {}; }
};

// The search box keeps what is being typed in its own state, so a keystroke re-renders only this small box. The table
// is filtered a moment after typing pauses, instead of re-drawing every row on each key.
function SearchBox({ initial, onSearch }) {
    const [v, setV] = useState(initial);
    useEffect(() => {
        const t = setTimeout(() => onSearch(v), 200);
        return () => clearTimeout(t);
    }, [v, onSearch]);
    return (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '7px', px: 1.4, height: 42, width: { xs: 180, sm: 340 } }}>
            <SearchRoundedIcon sx={{ fontSize: 18, color: '#98A0AE' }} />
            <InputBase placeholder="Search candidates…" value={v} onChange={(e) => setV(e.target.value)} sx={{ fontSize: 13, flex: 1 }} />
        </Stack>
    );
}

export default function CandidatesTab({ setActions }) {
    const navigate = useNavigate();
    const activeEntityId = useSelector(selectActiveEntityId);
    const [catPick, setCatPick] = useState('');
    const [candidates, setCandidates] = useState([]);
    const [openVacancies, setOpenVacancies] = useState([]);
    const [commMeta, setCommMeta] = useState({ types: [], statuses: [] });
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [saving, setSaving] = useState(false);

    // Filters are kept for the session so opening a candidate and coming back leaves them as they were.
    const saved = useRef(readSavedFilters()).current;
    const [q, setQ] = useState(saved.q ?? '');
    const [stageFilter, setStageFilter] = useState(saved.stageFilter ?? 'all');
    const [vacancyFilter, setVacancyFilter] = useState(saved.vacancyFilter ?? 'all');
    const [catFilter, setCatFilter] = useState('all');
    const [sourceFilter, setSourceFilter] = useState(saved.sourceFilter ?? 'all');
    const [dateMode, setDateMode] = useState(saved.dateMode ?? 'all'); // all | this | last | custom
    const [dateFrom, setDateFrom] = useState(saved.dateFrom ?? '');
    const [dateTo, setDateTo] = useState(saved.dateTo ?? '');
    useEffect(() => {
        try { sessionStorage.setItem(FILTER_KEY, JSON.stringify({ q, stageFilter, vacancyFilter, sourceFilter, dateMode, dateFrom, dateTo })); } catch { /* storage unavailable: filters just reset */ }
    }, [q, stageFilter, vacancyFilter, sourceFilter, dateMode, dateFrom, dateTo]);
    // A vacancy's job category is the one picked when it was created, else its own title.
    const catMap = useMemo(() => readVacancyCategories(activeEntityId), [activeEntityId, candidates]); // eslint-disable-line react-hooks/exhaustive-deps
    const catOfId = useCallback((id, title) => catMap[id] || title || 'Unknown role', [catMap]);
    const [dateAnchor, setDateAnchor] = useState(null); // opens the From/To picker under the dropdown
    const dateBox = useRef(null);
    const [dialog, setDialog] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [tried, setTried] = useState(false);
    const [snack, setSnack] = useState(null);
    const [logFor, setLogFor] = useState(null);
    const editReqRef = useRef(null); // id of the candidate whose employment refresh is current
    const resumeInput = useRef(null);

    const [confirm, setConfirm] = useState(null);
    const [stageAnchor, setStageAnchor] = useState(null);
    const [stageTarget, setStageTarget] = useState(null);
    const [reject, setReject] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    const [busyId, setBusyId] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const notify = (msg, sev = 'success') => setSnack({ msg, sev });

    const loadCandidates = useCallback(async () => {
        setLoading(true);
        try {
            const [{ data: body }, ivRes] = await Promise.all([
                http.get(GetCandidates),
                http.get(GetInterviews).catch(() => null), // stages fall back to the server's when this fails
            ]);
            if (body?.error) throw new Error(body.message || 'Could not load candidates.');
            const list = Array.isArray(body?.data?.candidates) ? body.data.candidates : [];
            const ivData = ivRes?.data?.error ? {} : (ivRes?.data?.data || {});
            const interviews = ['today', 'awaitingReview', 'upcoming', 'completed'].flatMap((k) => (Array.isArray(ivData[k]) ? ivData[k] : []));
            const normalized = withInterviewStage(list.map(normalizeCandidate), interviews);
            setCandidates(normalized);
            setCommMeta({
                types: Array.isArray(body?.data?.communicationTypes) ? body.data.communicationTypes : [],
                statuses: Array.isArray(body?.data?.communicationStatuses) ? body.data.communicationStatuses : [],
            });
            setLoadError('');
            return normalized;
        } catch (err) {
            setLoadError(errText(err, 'Could not load candidates.'));
        } finally {
            setLoading(false);
        }
    }, []);

    const loadVacancies = useCallback(async () => {
        try {
            const { data: body } = await http.get(GetVacancies);
            if (body?.error) return;
            const list = Array.isArray(body?.data?.vacancies) ? body.data.vacancies : [];
            setOpenVacancies(list
                .filter((v) => v.status === 'Open')
                .map((v) => ({ id: v.id, title: v.roleTitle, department: v.department, experience: v.experienceRange, location: v.location, open: Math.max(0, (Number(v.openings) || 0) - (Number(v.filled) || 0)) })));
        } catch { }
    }, []);

    useEffect(() => { loadCandidates(); loadVacancies(); }, [loadCandidates, loadVacancies]);

    // Search + Add Candidate live in the breadcrumb row, published up to the page.
    // The handler goes through a ref, so the published buttons are not rebuilt on every render.
    const createRef = useRef(null);
    const exportRef = useRef(null);
    createRef.current = () => openCreate(); // read when clicked, by which time openCreate (defined below) exists
    const savedSearch = useRef(q);
    useEffect(() => {
        if (!setActions) return undefined;
        setActions(
            <>
                <SearchBox initial={savedSearch.current} onSearch={setQ} />
                <Button startIcon={<FileDownloadRoundedIcon sx={{ color: '#7C5CFC' }} />} onClick={() => exportRef.current?.()} sx={{ ...ghostBtn, height: 42, px: 1.8, fontSize: 13, color: '#334155', '&:hover': { bgcolor: '#F1EEFE', borderColor: '#C9BEFB', color: '#7C5CFC' } }}>Export</Button>
                <Button startIcon={<AddRoundedIcon />} onClick={() => createRef.current?.()} sx={{ ...solidBtn, height: 42, px: 1.8, fontSize: 13 }}>Add Candidate</Button>
            </>,
        );
        return () => setActions(null);
    }, [setActions]);

    // Keep an open communication-log dialog in sync with a fresh reload.
    useEffect(() => {
        if (!logFor) return;
        const fresh = candidates.find((c) => c.id === logFor.id);
        if (fresh && fresh !== logFor) setLogFor(fresh);
    }, [candidates, logFor]);

    // One lower-cased search string per candidate, built once when the list changes, so each search is a plain includes().
    const searchText = useMemo(() => new Map(candidates.map((c) => [c.id, [c.name, c.email, c.phone, c.currentCompany, c.vacancyTitle, c.city].map((x) => String(x || '')).join(' ').toLowerCase()])), [candidates]);
    const deferredQ = useDeferredValue(q);

    const filtered = useMemo(() => {
        const s = deferredQ.trim().toLowerCase();
        const [rangeFrom, rangeTo] = dateRangeFor(dateMode, dateFrom, dateTo);
        return candidates.filter((c) => {
            if (stageFilter !== 'all' && c.stage !== stageFilter) return false;
            if (catFilter !== 'all' && catOfId(c.vacancyId, c.vacancyTitle) !== catFilter) return false;
            if (vacancyFilter !== 'all' && String(c.vacancyId) !== vacancyFilter) return false;
            if (sourceFilter !== 'all' && c.source !== sourceFilter) return false;
            if (rangeFrom || rangeTo) {
                const d = appliedISO(c.appliedOn);
                if (!d || (rangeFrom && d < rangeFrom) || (rangeTo && d > rangeTo)) return false;
            }
            if (!s) return true;
            return (searchText.get(c.id) || '').includes(s);
        });
    }, [candidates, searchText, deferredQ, stageFilter, vacancyFilter, catFilter, catOfId, sourceFilter, dateMode, dateFrom, dateTo]);

    const sourceChoices = useMemo(() => [...new Set(candidates.map((c) => c.source).filter(Boolean))].sort(), [candidates]);

    // Exports exactly the rows the current search and filters leave on screen.
    const exportCandidates = () => {
        if (!filtered.length) { notify('There are no candidates to export.', 'warning'); return; }
        const headers = ['S.No', 'Name', 'Email', 'Phone', 'Applying for', 'Stage', 'Source', 'Applied on', 'Experience (yrs)', 'Current company', 'City', 'Expected salary', 'Notice period'];
        const rows = filtered.map((c, i) => [
            i + 1, c.name, c.email, c.phone, c.vacancyTitle, (STAGES[c.stage] || STAGES.applied).label, c.source, c.appliedOn,
            c.isFresher ? 'Fresher' : c.experienceYears, c.currentCompany, c.city, c.expectedSalary || '', c.noticePeriod,
        ]);
        const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
        ws['!cols'] = [6, 24, 28, 14, 26, 14, 14, 14, 14, 22, 16, 14, 14].map((wch) => ({ wch }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Candidates');
        XLSX.writeFile(wb, `Candidates_${new Date().toISOString().slice(0, 10)}.xlsx`);
        notify(`Exported ${filtered.length} candidate${filtered.length === 1 ? '' : 's'}.`);
    };
    exportRef.current = exportCandidates;

    // Open vacancies plus any a candidate belongs to.
    const vacancyChoices = useMemo(() => {
        const seen = new Map();
        // Every open vacancy is offered, even before it has candidates, so choosing a category lists all of its vacancies.
        openVacancies.forEach((v) => { if (v.id != null && !seen.has(String(v.id))) seen.set(String(v.id), v.title || 'Unknown role'); });
        candidates.forEach((c) => { if (c.vacancyId != null && !seen.has(String(c.vacancyId))) seen.set(String(c.vacancyId), c.vacancyTitle || 'Unknown role'); });
        return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
    }, [candidates, openVacancies]);
    const filterCategories = useMemo(() => [...new Set(vacancyChoices.map(([id, title]) => catOfId(id, title)))].sort((a, b) => String(a).localeCompare(String(b))), [vacancyChoices, catOfId]);
    // Openings under the chosen category; numbered when the category has more than one.
    const openingChoices = useMemo(() => {
        const list = vacancyChoices.filter(([id, title]) => catFilter === 'all' || catOfId(id, title) === catFilter);
        return list.map(([id, title], i) => [id, catFilter !== 'all' && list.length > 1 ? `Opening ${i + 1} · ${title}` : title]);
    }, [vacancyChoices, catFilter, catOfId]);

    const openCreate = () => { loadVacancies(); setCatPick(''); setForm(EMPTY); setTried(false); setDialog({ mode: 'create' }); };
    const closeDialog = () => { if (!saving) { editReqRef.current = null; setCatPick(''); setDialog(null); } };
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const setCommRow = (i, key) => (e) => setForm((f) => ({
        ...f, communications: f.communications.map((row, idx) => (idx === i ? { ...row, [key]: e.target.value } : row)),
    }));
    const addCommRow = () => setForm((f) => (f.communications.length >= MAX_COMM_ROWS ? f : { ...f, communications: [...f.communications, emptyCommRow()] }));
    const editCommRow = (i) => setForm((f) => ({
        ...f, communications: f.communications.map((row, idx) => (idx === i ? { ...row, editing: true } : row)),
    }));
    // A picked resume is previewed from this device and uploaded (UploadCandidateResume)
    // when the candidate is saved; the saved one opens from its stored URL.
    const pickResume = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        const ext = `.${file.name.split('.').pop().toLowerCase()}`;
        if (!RESUME_TYPES.includes(ext)) { notify('Resume must be a PDF, DOC or DOCX file.', 'warning'); return; }
        if (file.size > MAX_RESUME_BYTES) { notify('Resume must be 10 MB or smaller.', 'warning'); return; }
        setForm((f) => ({ ...f, resume: file }));
    };
    const openResume = () => {
        if (!form.resume) {
            if (dialog?.savedResume?.url) window.open(dialog.savedResume.url, '_blank', 'noopener');
            return;
        }
        const url = URL.createObjectURL(form.resume);
        window.open(url, '_blank', 'noopener');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
    };
    const removeCommRow = (i) => setForm((f) => ({ ...f, communications: f.communications.filter((_, idx) => idx !== i) }));

    const setEmpRow = (i, key) => (e) => setForm((f) => ({
        ...f, employments: f.employments.map((row, idx) => (idx === i ? { ...row, [key]: e.target.value } : row)),
    }));
    const setEmpValue = (i, key, value) => setForm((f) => ({
        ...f, employments: f.employments.map((row, idx) => (idx === i ? { ...row, [key]: value } : row)),
    }));
    const toggleEmpCurrent = (i) => (e) => setForm((f) => ({
        ...f, employments: f.employments.map((row, idx) => (idx === i ? { ...row, current: e.target.checked, toMonth: e.target.checked ? '' : row.toMonth, reasonForLeaving: e.target.checked ? '' : row.reasonForLeaving } : row)),
    }));
    const addEmpRow = () => { setForm((f) => (f.employments.length >= MAX_EMP_ROWS ? f : { ...f, employments: [...f.employments, emptyEmpRow()] })); };
    const removeEmpRow = (i) => setForm((f) => ({ ...f, employments: f.employments.filter((_, idx) => idx !== i) }));

    const vacancyOptions = useMemo(() => {
        if (dialog?.mode === 'edit' && form.vacancyId && !openVacancies.some((v) => v.id === form.vacancyId)) {
            return [{ id: form.vacancyId, title: dialog.vacancyTitle || 'Current vacancy', department: '', open: 0, closed: true }, ...openVacancies];
        }
        return openVacancies;
    }, [dialog, form.vacancyId, openVacancies]);

    // Job category first, then the specific opening under it (a category such as Accounts can hold several openings).
    const catOf = useCallback((v) => readVacancyCategories(activeEntityId)[v.id] || v.title, [activeEntityId]);
    const categoryChoices = useMemo(() => [...new Set(vacancyOptions.map(catOf).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b))), [vacancyOptions, catOf]);
    const slabOptions = useMemo(() => vacancyOptions.filter((v) => catOf(v) === catPick).sort((a, b) => Number(a.id) - Number(b.id)), [vacancyOptions, catOf, catPick]);
    // Editing: start on the category the candidate's vacancy belongs to.
    useEffect(() => {
        if (!dialog || catPick || !form.vacancyId) return;
        const v = vacancyOptions.find((x) => x.id === form.vacancyId);
        if (v) setCatPick(catOf(v));
    }, [dialog, form.vacancyId, vacancyOptions, catPick, catOf]);
    const pickCategory = (e) => {
        const c = e.target.value;
        setCatPick(c);
        const slabs = vacancyOptions.filter((v) => catOf(v) === c);
        setForm((f) => ({ ...f, vacancyId: slabs.length === 1 ? slabs[0].id : '' }));
    };

    const filledCommRows = form.communications.filter(commRowFilled);
    const invalidCommRows = filledCommRows.filter(commRowActive).some((row) => !commRowValid(row));
    const filledEmpRows = form.employments.filter(empRowFilled);
    const invalidEmpRows = filledEmpRows.some((row) => !empRowValid(row));
    const valid = form.name.trim() && form.vacancyId && !invalidCommRows && !invalidEmpRows;

    // Edit mode: reconcile the Work Experience rows with what is stored — update the
    // rows that kept their id, add the new ones, delete the ones that were removed.
    const syncEmployments = async (candidateId, saved, rows) => {
        const keptIds = new Set(rows.filter((r) => r.id != null).map((r) => r.id));
        const removed = saved.filter((r) => r.id != null && !keptIds.has(r.id));
        const failures = [];
        const run = async (fn) => {
            try {
                const { data: body } = await fn();
                if (body?.error) failures.push(body.message || 'request failed');
            } catch (err) {
                failures.push(errText(err, 'request failed'));
            }
        };
        await Promise.all([
            ...removed.map((r) => run(() => http.delete(DeleteCandidateEmployment, { params: { id: r.id } }))),
            ...rows.map((r) => run(() => (r.id != null
                ? http.put(UpdateCandidateEmployment, { id: r.id, candidateId, ...empPayload(r) })
                : http.post(AddCandidateEmployment, { candidateId, ...empPayload(r) })))),
        ]);
        if (failures.length) notify(`Candidate saved, but ${failures.length} work experience change(s) failed: ${failures[0]}`, 'warning');
    };

    // Edit mode: new rows are added, rows opened with the pencil are updated, rows
    // the user removed are deleted. Untouched saved rows are left alone.
    const syncCommunications = async (candidateId, saved, rows) => {
        const keptIds = new Set(rows.filter((r) => r.id != null).map((r) => r.id));
        const removed = saved.filter((r) => r.id != null && !keptIds.has(r.id));
        const failures = [];
        const run = async (fn) => {
            try {
                const { data: body } = await fn();
                if (body?.error) failures.push(body.message || 'request failed');
            } catch (err) {
                failures.push(errText(err, 'request failed'));
            }
        };
        await Promise.all([
            ...removed.map((r) => run(() => http.delete(DeleteCandidateCommunication, { params: { id: r.id } }))),
            ...rows.filter(commRowActive).map((r) => run(() => (r.id != null
                ? http.put(UpdateCandidateCommunication, { id: r.id, candidateId, ...commPayload(r) })
                : http.post(AddCandidateCommunication, { candidateId, ...commPayload(r) })))),
        ]);
        if (failures.length) notify(`Candidate saved, but ${failures.length} communication change(s) failed: ${failures[0]}`, 'warning');
    };

    const submit = async () => {
        setTried(true);
        if (!valid) {
            notify(!form.name.trim() ? 'The candidate needs a name.'
                : !form.vacancyId ? 'Pick the vacancy they are applying for.'
                    : invalidEmpRows ? 'Every work experience entry needs a company, designation, start and end month, and a reason for leaving (or tick I currently work here).'
                        : 'Every communication row needs a type, a status, and a follow-up date if one is scheduled.', 'warning');
            return;
        }

        const payload = {
            vacancyId: form.vacancyId,
            fullName: form.name.trim(),
            // The server treats a blank email as one shared address, so a second candidate without one is rejected
            // as "already exists" before it is saved. A blank gets a unique placeholder, as the import does.
            // The time + random suffix keeps it unique even when the name and phone match an existing candidate.
            email: form.email.trim() || `${String(form.phone || '').replace(/\D/g, '') || 'cand'}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}@noemail.local`,
            phone: form.phone || '',
            currentCompany: form.currentCompany || '',
            city: (form.city || '').trim(),
            experience: Number(form.experience) || 0,
            currentSalary: Number(form.currentSalary) || 0,
            expectedSalary: Number(form.expectedSalary) || 0,
            noticePeriod: form.noticePeriod,
            source: form.source,
        };
        const editing = dialog?.mode === 'edit';
        if (!editing && filledCommRows.length > 0) {
            payload.communications = filledCommRows.map(commPayload);
        }
        if (!editing && filledEmpRows.length > 0) {
            payload.employments = filledEmpRows.map(empPayload);
        }

        setSaving(true);
        try {
            // Upload a newly picked resume first; its stored path/url go on the candidate.
            // With no new file the resume fields are left out, so an attached one stays.
            if (form.resume) {
                const fd = new FormData();
                fd.append('file', form.resume);
                const { data: up } = await http.post(UploadCandidateResume, fd);
                if (up?.error) throw new Error(up.message || 'Could not upload the resume.');
                const r = up?.data ?? up ?? {};
                payload.resumeFileName = r.fileName ?? form.resume.name;
                payload.resumePath = r.path ?? '';
                payload.resumeUrl = r.url ?? '';
            }
            const { data: body } = editing
                ? await http.put(UpdateCandidate, { id: dialog.id, ...payload })
                : await http.post(AddCandidate, payload);
            if (body?.error) throw new Error(body.message || 'Could not save the candidate.');
            const vacTitle = vacancyOptions.find((v) => v.id === form.vacancyId)?.title || 'the vacancy';
            notify(body?.message || (editing ? `${payload.fullName} updated.` : `${payload.fullName} added to ${vacTitle}.`));
            setDialog(null);
            if (editing) {
                await syncEmployments(dialog.id, dialog.savedEmployments || [], filledEmpRows);
                await syncCommunications(dialog.id, dialog.savedCommunications || [], filledCommRows);
            }
            await loadCandidates();
        } catch (err) {
            notify(errText(err, 'Could not save the candidate.'), 'error');
        } finally {
            setSaving(false);
        }
    };

    // Only Applied <-> Rejected can be set by hand; every later stage follows the interview rounds.
    const setStage = async (candidate, stage, reason = '') => {
        if (busyId) return;
        setBusyId(candidate.id);
        try {
            const { data: body } = await http.put(SetCandidateStage, {
                id: candidate.id,
                stage,
                rejectReason: stage === 'Rejected' ? reason.trim() : '',
            });
            if (body?.error) throw new Error(body.message || 'Could not update the stage.');
            notify(`${candidate.name} moved to ${stage}.`);
            setReject(null);
            setRejectReason('');
            await loadCandidates();
        } catch (err) {
            notify(errText(err, 'Could not update the stage.'), 'error');
        } finally {
            setBusyId(null);
        }
    };

    const pickStage = (key) => {
        const candidate = stageTarget;
        setStageAnchor(null);
        setStageTarget(null);
        if (!candidate || key === candidate.stage) return;
        if (key === 'rejected') { setRejectReason(''); setReject(candidate); return; }
        setStage(candidate, 'Applied');
    };

    const requestDelete = (c) => {
        if (c.stage !== 'rejected') {
            notify('Only rejected candidates can be deleted. Reject this candidate first.', 'warning');
            return;
        }
        setConfirm(c);
    };

    const doDelete = async () => {
        if (!confirm || deleting) return;
        setDeleting(true);
        try {
            const { data: body } = await http.delete(DeleteCandidate, { params: { id: confirm.id } });
            if (body?.error) throw new Error(body.message || 'Could not remove the candidate.');
            notify(body?.message || `${confirm.name} removed.`);
            setConfirm(null);
            await loadCandidates();
        } catch (err) {
            notify(errText(err, 'Could not remove the candidate.'), 'error');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Box sx={{ p: 2, pt: 1.5 }}>
            <Panel
                title="Candidates"
                icon={PeopleAltRoundedIcon}
                chip={`${filtered.length} of ${candidates.length}`}
                action={(
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Box sx={{ width: 190 }}>
                            <SearchSelect
                                value={catFilter} onChange={(e) => { setCatFilter(e.target.value); setVacancyFilter('all'); }} searchPlaceholder="Search job categories…"
                                sx={{ ...field, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 38, bgcolor: '#fff' } }}
                                options={[{ value: 'all', label: 'All job categories' }, ...filterCategories.map((c) => ({ value: c, label: c }))]}
                            />
                        </Box>
                        <Box sx={{ width: 190 }}>
                            <SearchSelect
                                value={vacancyFilter} onChange={(e) => setVacancyFilter(e.target.value)} searchPlaceholder="Search vacancies…"
                                sx={{ ...field, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 38, bgcolor: '#fff' } }}
                                options={[{ value: 'all', label: 'All openings' }, ...openingChoices.map(([id, title]) => ({ value: id, label: title }))]}
                            />
                        </Box>
                        <Box ref={dateBox}>
                            <TextField
                                select size="small" value={dateMode}
                                onChange={(e) => { setDateMode(e.target.value); if (e.target.value !== 'custom') { setDateFrom(''); setDateTo(''); } }}
                                slotProps={{ select: { renderValue: (v) => (v === 'custom' && (dateFrom || dateTo) ? `${dateFrom ? dateFrom.split('-').reverse().join('-') : '…'} – ${dateTo ? dateTo.split('-').reverse().join('-') : '…'}` : ({ all: 'All dates', today: 'Today', this: 'This month', last: 'Last month', custom: 'Custom range' })[v]) } }}
                                sx={{ ...field, width: 190, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 38, bgcolor: '#fff' } }}
                            >
                                <MenuItem value="all" sx={{ fontSize: 13 }}>All dates</MenuItem>
                                <MenuItem value="today" sx={{ fontSize: 13 }}>Today</MenuItem>
                                <MenuItem value="this" sx={{ fontSize: 13 }}>This month</MenuItem>
                                <MenuItem value="last" sx={{ fontSize: 13 }}>Last month</MenuItem>
                                <MenuItem value="custom" onClick={() => setDateAnchor(dateBox.current)} sx={{ fontSize: 13 }}>Custom range</MenuItem>
                            </TextField>
                            <Popover
                                open={Boolean(dateAnchor)} anchorEl={dateAnchor} onClose={() => setDateAnchor(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                slotProps={{ paper: { sx: { p: 2, mt: 0.5, width: 270, borderRadius: '12px' } } }}
                            >
                                <Stack spacing={1.6}>
                                    <TextField label="From" type="date" size="small" fullWidth value={dateFrom} sx={field}
                                        slotProps={{ inputLabel: { shrink: true } }}
                                        onChange={(e) => { setDateFrom(e.target.value); if (dateTo && e.target.value > dateTo) setDateTo(''); }} />
                                    <TextField label="To" type="date" size="small" fullWidth value={dateTo} sx={field}
                                        slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: dateFrom || undefined } }}
                                        onChange={(e) => { setDateTo(e.target.value); if (/^\d{4}-\d{2}-\d{2}$/.test(e.target.value) && Number(e.target.value.slice(0, 4)) >= 1900) setDateAnchor(null); }} />
                                </Stack>
                            </Popover>
                        </Box>
                        <TextField
                            select size="small" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}
                            sx={{ ...field, width: 140, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 38, bgcolor: '#fff' } }}
                        >
                            <MenuItem value="all" sx={{ fontSize: 13 }}>All sources</MenuItem>
                            {sourceChoices.map((x) => <MenuItem key={x} value={x} sx={{ fontSize: 13 }}>{x}</MenuItem>)}
                        </TextField>
                        <TextField
                            select size="small" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}
                            sx={{ ...field, width: 150, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 38, bgcolor: '#fff' } }}
                        >
                            <MenuItem value="all" sx={{ fontSize: 13 }}>All stages</MenuItem>
                            {Object.entries(STAGES).map(([k, s]) => <MenuItem key={k} value={k} sx={{ fontSize: 13 }}>{s.label}</MenuItem>)}
                        </TextField>
                    </Stack>
                )}
            >
                {loading ? (
                    <SkeletonRows count={5} />
                ) : loadError ? (
                    <Box sx={{ p: 3 }}>
                        <LoadAlert sx={{ borderRadius: '9px' }}
                            action={<Button size="small" onClick={loadCandidates} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}>
                            {loadError}
                        </LoadAlert>
                    </Box>
                ) : filtered.length === 0 ? (
                    <EmptyState
                        icon={PeopleAltRoundedIcon}
                        title={q || stageFilter !== 'all' || vacancyFilter !== 'all' || catFilter !== 'all' || sourceFilter !== 'all' || dateMode !== 'all' ? 'No candidates match these filters' : 'No candidates yet'}
                        hint={q || stageFilter !== 'all' || vacancyFilter !== 'all' || catFilter !== 'all' || sourceFilter !== 'all' || dateMode !== 'all' ? 'Clear the search or change a filter.' : 'Add an applicant against an open vacancy, then schedule their first round.'}
                    />
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box component="table" sx={{ width: '100%', minWidth: 940, borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                            <Box component="thead" sx={{ bgcolor: '#F4F3FB' }}>
                                <Box component="tr">
                                    {['CANDIDATE', 'APPLIED FOR', 'APPLIED DATE', 'SOURCE', 'LOCATION', 'EXPERIENCE', 'STAGE', 'ACTIONS'].map((h) => (
                                        <Box component="th" key={h} sx={{ ...th, textAlign: h === 'ACTIONS' ? 'right' : 'left', width: COL_W[h] }}>{h}</Box>
                                    ))}
                                </Box>
                            </Box>
                            <Box component="tbody">
                                {filtered.map((c, i) => {
                                    const stage = STAGES[c.stage] || STAGES.applied;
                                    return (
                                        <Box component="tr" key={c.id}
                                            onClick={(e) => { if (!e.target.closest('button, a, [role="button"]')) navigate(`/dashboard/recruitment/candidates/${c.id}`); }}
                                            sx={{ bgcolor: i % 2 ? '#FBFAFE' : '#fff', cursor: 'pointer', '&:hover': { bgcolor: '#F5F2FF' } }}>
                                            <Box component="td" sx={cell}>
                                                <Stack direction="row" spacing={1.3} sx={{ alignItems: 'center' }}>
                                                    <Avatar sx={{ width: 38, height: 38, ...avatarTone(c.name), fontSize: 13, fontWeight: 700 }}>{initials(c.name)}</Avatar>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0F172A' }}>{c.name}</Typography>
                                                        <Typography sx={{ fontSize: 11, color: '#98A0AE' }} noWrap>{c.phone || c.email || '—'}</Typography>
                                                    </Box>
                                                </Stack>
                                            </Box>
                                            <Box component="td" sx={cell}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{c.vacancyTitle || 'Unknown role'}</Typography>
                                            </Box>
                                            <Box component="td" sx={cell}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{c.appliedOn || '—'}</Typography>
                                            </Box>
                                            <Box component="td" sx={cell}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{c.source || '—'}</Typography>
                                            </Box>
                                            <Box component="td" sx={cell}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{spaced(titleCase(c.city)) || '—'}</Typography>
                                            </Box>
                                            <Box component="td" sx={cell}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{expText(c)}</Typography>
                                            </Box>
                                            <Box component="td" sx={cell}>
                                                {/* In the interview process the chip names the round the candidate is at; the outcome sits beneath it. */}
                                                {(() => {
                                                    const inRounds = c.stageRound && c.stage !== 'rejected';
                                                    const editableStage = c.stage === 'applied' || c.stage === 'rejected';
                                                    const sub = c.stage === 'interviewing' ? 'In progress' : stage.label;
                                                    return (
                                                        <>
                                                            <Chip
                                                                label={inRounds ? c.stageRound : stage.label} size="small"
                                                                {...(editableStage ? {
                                                                    clickable: true,
                                                                    disabled: busyId === c.id,
                                                                    onClick: (e) => { setStageTarget(c); setStageAnchor(e.currentTarget); },
                                                                    onDelete: (e) => { setStageTarget(c); setStageAnchor(e.currentTarget); },
                                                                    deleteIcon: busyId === c.id ? <CircularProgress size={12} sx={{ color: stage.color }} /> : <KeyboardArrowDownRoundedIcon sx={{ fontSize: 15 }} />,
                                                                } : {})}
                                                                sx={{ height: 24, fontSize: 11, fontWeight: 700, bgcolor: stage.bg, color: stage.color, '& .MuiChip-deleteIcon': { color: stage.color, '&:hover': { color: stage.color } } }}
                                                            />
                                                            {inRounds && <Typography sx={{ fontSize: 10.5, color: '#98A0AE', mt: 0.3 }} noWrap>{sub}</Typography>}
                                                            {c.stage === 'rejected' && c.stageRound && <Typography sx={{ fontSize: 10.5, color: '#98A0AE', mt: 0.3 }} noWrap>{c.stageRound}</Typography>}
                                                        </>
                                                    );
                                                })()}
                                                {c.stage === 'rejected' && c.rejectReason && (
                                                    <Tooltip arrow title={c.rejectReason}>
                                                        <Typography sx={{ fontSize: 10.5, color: '#E11D48', mt: 0.3, maxWidth: 150, cursor: 'help' }} noWrap>{c.rejectReason}</Typography>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                            <Box component="td" sx={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                <Tooltip arrow title="Communication log">
                                                    <IconButton size="small" onClick={() => setLogFor(c)} sx={{ color: c.communications.length ? '#7C5CFC' : '#94A3B8', '&:hover': { color: '#7C5CFC', bgcolor: '#F1EEFE' } }}>
                                                        {c.communications.length > 0 ? (
                                                            <Chip label={c.communications.length} size="small" icon={<ChatBubbleOutlineRoundedIcon sx={{ fontSize: '13px !important' }} />}
                                                                sx={{ height: 22, fontSize: 10.5, fontWeight: 700, bgcolor: '#F1EEFE', color: '#7C5CFC', '& .MuiChip-icon': { color: 'inherit', ml: '5px' }, '& .MuiChip-label': { px: '5px' } }} />
                                                        ) : <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 17 }} />}
                                                    </IconButton>
                                                </Tooltip>
                                                {c.stage === 'rejected' && (
                                                    <Tooltip arrow title="Remove">
                                                        <IconButton size="small" onClick={() => requestDelete(c)} sx={{ ml: 0.4, color: '#94A3B8', '&:hover': { color: '#E11D48', bgcolor: '#FEF2F2' } }}><DeleteOutlineRoundedIcon sx={{ fontSize: 17 }} /></IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Box>
                )}
            </Panel>

            <Dialog open={Boolean(dialog)} onClose={closeDialog} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A' }}>{dialog?.mode === 'edit' ? 'Edit Candidate' : 'Add Candidate'}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.2 }}>
                        {dialog?.mode === 'edit' ? 'Update the applicant’s details. Their stage is changed from the Interviews page. Fields marked * are required; the rest are optional.' : 'They start as Applied, and move to Interviewing once you schedule their first round. Fields marked * are required; the rest are optional.'}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    <Grid container columnSpacing={2} rowSpacing={2.5}>
                        <Grid size={12}>
                            <TextField select required label="Job category" size="small" fullWidth slotProps={{ inputLabel: { shrink: true } }} value={catPick} onChange={pickCategory}
                                error={tried && !catPick} sx={dlgField}
                                helperText={categoryChoices.length === 0 ? 'No open vacancies — open one on the Vacancies tab first.' : undefined}>
                                {categoryChoices.length === 0 && <MenuItem disabled sx={{ fontSize: 13 }}>No open categories</MenuItem>}
                                {categoryChoices.map((c) => <MenuItem key={c} value={c} sx={{ fontSize: 13.5 }}>{c}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid size={12}>
                            <TextField select required label="Vacancy" size="small" fullWidth slotProps={{ inputLabel: { shrink: true } }} value={slabOptions.some((v) => v.id === form.vacancyId) ? form.vacancyId : ''} onChange={set('vacancyId')}
                                disabled={!catPick} error={tried && !form.vacancyId} sx={dlgField}
                                helperText={!catPick ? 'Choose a job category first.' : undefined}>
                                {slabOptions.map((v, i) => (
                                    <MenuItem key={v.id} value={v.id} sx={{ fontSize: 13.5 }}>
                                        {slabOptions.length > 1 ? `Opening ${i + 1} · ` : ''}{v.title}{v.department ? ` — ${v.department}` : ''}{v.experience ? ` · ${v.experience}` : ''}{v.closed ? ' (closed)' : ` (${v.open} open)`}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField required label="Full name" size="small" fullWidth slotProps={{ inputLabel: { shrink: true } }} value={form.name} onChange={set('name')} error={tried && !form.name.trim()} sx={dlgField} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Email" size="small" fullWidth value={form.email} onChange={set('email')}
                                slotProps={{ inputLabel: { shrink: true }, input: { startAdornment: <InputAdornment position="start"><MailRoundedIcon sx={{ fontSize: 16, color: '#94A3B8' }} /></InputAdornment> } }} sx={dlgField} />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Phone" size="small" fullWidth value={form.phone} onChange={set('phone')}
                                slotProps={{ inputLabel: { shrink: true }, input: { startAdornment: <InputAdornment position="start"><CallRoundedIcon sx={{ fontSize: 16, color: '#94A3B8' }} /></InputAdornment> } }} sx={dlgField} />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField select label="Source" size="small" fullWidth slotProps={{ inputLabel: { shrink: true } }} value={form.source} onChange={set('source')} sx={dlgField}>
                                {SOURCES.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: 13.5 }}>{s}</MenuItem>)}
                            </TextField>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Current company" size="small" fullWidth value={form.currentCompany} onChange={set('currentCompany')} placeholder="Leave blank for a fresher" slotProps={{ inputLabel: { shrink: true } }} sx={dlgField} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Location" size="small" fullWidth value={form.city} onChange={set('city')} placeholder="City, e.g. Thanjavur"
                                slotProps={{ inputLabel: { shrink: true }, input: { startAdornment: <InputAdornment position="start"><PlaceRoundedIcon sx={{ fontSize: 16, color: '#94A3B8' }} /></InputAdornment> } }} sx={dlgField} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Experience (years)" type="number" size="small" fullWidth value={form.experience} onChange={set('experience')} placeholder="e.g. 5" slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: 0, step: 0.5 } }} sx={dlgField} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Notice period" size="small" fullWidth placeholder="e.g. 30 days" slotProps={{ inputLabel: { shrink: true } }} value={form.noticePeriod} onChange={set('noticePeriod')} sx={dlgField} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Current salary" type="number" size="small" fullWidth value={form.currentSalary} onChange={set('currentSalary')}
                                slotProps={{ inputLabel: { shrink: true }, input: { startAdornment: <InputAdornment position="start">₹</InputAdornment> } }} sx={dlgField} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Expected salary" type="number" size="small" fullWidth value={form.expectedSalary} onChange={set('expectedSalary')}
                                slotProps={{ inputLabel: { shrink: true }, input: { startAdornment: <InputAdornment position="start">₹</InputAdornment> } }} sx={dlgField} />
                        </Grid>


                        <Grid size={12}>
                            <input ref={resumeInput} type="file" hidden accept={RESUME_TYPES.join(',')} onChange={pickResume} />
                            {(form.resume || dialog?.savedResume) ? (
                                <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center', px: 1.6, py: 1, borderRadius: '9px', border: '1px solid #EEF0F6', bgcolor: '#FAFAFD' }}>
                                    <DescriptionRoundedIcon sx={{ fontSize: 22, color: '#7C5CFC' }} />
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography noWrap sx={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>{form.resume ? form.resume.name : dialog.savedResume.fileName}</Typography>
                                        <Typography sx={{ fontSize: 11, color: '#98A0AE' }}>{form.resume ? `New resume · ${fmtSize(form.resume.size)}${dialog?.savedResume ? ' · replaces the current one' : ''}` : 'Resume on file'}</Typography>
                                    </Box>
                                    <Tooltip arrow title="Open in browser">
                                        <IconButton size="small" onClick={openResume} sx={{ color: '#94A3B8', '&:hover': { color: '#7C5CFC', bgcolor: '#F1EEFE' } }}>
                                            <OpenInNewRoundedIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip arrow title="Replace">
                                        <IconButton size="small" onClick={() => resumeInput.current?.click()} sx={{ color: '#94A3B8', '&:hover': { color: '#7C5CFC', bgcolor: '#F1EEFE' } }}>
                                            <UploadFileRoundedIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                    {form.resume && (
                                        <Tooltip arrow title="Remove">
                                            <IconButton size="small" onClick={() => setForm((f) => ({ ...f, resume: null }))} sx={{ color: '#94A3B8', '&:hover': { color: '#E11D48', bgcolor: '#FEF2F2' } }}>
                                                <CloseRoundedIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Stack>
                            ) : (
                                <Button onClick={() => resumeInput.current?.click()} startIcon={<UploadFileRoundedIcon sx={{ fontSize: 18, color: '#94A3B8' }} />}
                                    sx={{
                                        height: 40, px: 1.8, width: '100%', justifyContent: 'flex-start', textTransform: 'none', borderRadius: '7px',
                                        bgcolor: '#F8FAFC', border: '1px dashed #D8DEE8', color: '#475569', fontSize: 13.5, fontWeight: 500,
                                        '&:hover': { bgcolor: '#F1F5F9', borderColor: '#B8C0CC' },
                                    }}>
                                    Upload resume
                                    <Box component="span" sx={{ ml: 1, fontSize: 11.5, color: '#98A0AE', fontWeight: 400 }}>PDF, DOC or DOCX · up to 10 MB</Box>
                                </Button>
                            )}
                        </Grid>

                        <Grid size={12}>
                            <Stack direction="row" spacing={1.2} sx={{ flexWrap: 'wrap', gap: 1.2, mt: 0.5 }}>
                                <Button
                                    onClick={addEmpRow} disabled={form.employments.length >= MAX_EMP_ROWS}
                                    startIcon={<WorkHistoryRoundedIcon sx={{ fontSize: 16 }} />} sx={{ ...tonalBtn, height: 36, fontSize: 12.5 }}
                                >
                                    Add Experience
                                    <Chip label={`${form.employments.length}/${MAX_EMP_ROWS}`} size="small" sx={{ ml: 1, height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#fff', color: 'inherit' }} />
                                </Button>
                                <Button
                                    onClick={addCommRow} disabled={form.communications.length >= MAX_COMM_ROWS}
                                    startIcon={<ChatBubbleOutlineRoundedIcon sx={{ fontSize: 16 }} />} sx={{ ...tonalBtn, height: 36, fontSize: 12.5 }}
                                >
                                    Add Communication
                                    <Chip label={`${form.communications.length}/${MAX_COMM_ROWS}`} size="small" sx={{ ml: 1, height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#fff', color: 'inherit' }} />
                                </Button>
                            </Stack>
                        </Grid>

                        <Grid size={12} sx={{ display: form.employments.length ? 'block' : 'none' }}>
                                {form.employments.length > 0 && (
                                    <Stack spacing={2}>
                                    {form.employments.map((row, i) => (
                                        <Box key={i} sx={{ px: 2, py: 2.4, borderRadius: '9px', border: '1px solid #EEF0F6', bgcolor: '#FAFAFD' }}>
                                            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                                <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center' }}>
                                                    <Chip label={`Experience ${i + 1}`} size="small" sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: '#F1EEFE', color: '#7C5CFC' }} />
                                                    {row.fromMonth && <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#98A0AE' }}>{durationText(empMonths(row))}</Typography>}
                                                </Stack>
                                                <IconButton size="small" onClick={() => removeEmpRow(i)} sx={{ color: '#94A3B8', '&:hover': { color: '#E11D48', bgcolor: '#FEF2F2' } }}>
                                                    <CloseRoundedIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Stack>
                                            <Grid container columnSpacing={2} rowSpacing={3}>
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <TextField required label="Company name" size="small" fullWidth value={row.company} onChange={setEmpRow(i, 'company')}
                                                        error={tried && empRowFilled(row) && !row.company.trim()} sx={empField} />
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <TextField required label="Designation" size="small" fullWidth value={row.designation} onChange={setEmpRow(i, 'designation')}
                                                        error={tried && empRowFilled(row) && !row.designation.trim()} sx={empField} />
                                                </Grid>

                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <MonthYearField
                                                        label="From *" value={row.fromMonth}
                                                        onChange={(v) => setEmpValue(i, 'fromMonth', v)}
                                                        error={tried && empRowFilled(row) && !isYM(row.fromMonth)}
                                                    />
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    {row.current ? (
                                                        <Box sx={{ height: 40, display: 'flex', alignItems: 'center', px: 1.6, borderRadius: '7px', border: '1px dashed #D8DEE8', bgcolor: '#fff' }}>
                                                            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: '#7C5CFC' }}>Present</Typography>
                                                        </Box>
                                                    ) : (
                                                        <MonthYearField
                                                            label="To *" value={row.toMonth}
                                                            onChange={(v) => setEmpValue(i, 'toMonth', v)}
                                                            error={tried && empRowFilled(row) && (!isYM(row.toMonth) || row.toMonth < row.fromMonth)}
                                                        />
                                                    )}
                                                </Grid>
                                                <Grid size={12} sx={{ mt: -1 }}>
                                                    <FormControlLabel
                                                        control={<Checkbox size="small" checked={row.current} onChange={toggleEmpCurrent(i)} />}
                                                        label={<Typography sx={{ fontSize: 12.5, color: '#475569' }}>I currently work here</Typography>}
                                                        sx={{ m: 0 }}
                                                    />
                                                </Grid>

                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <TextField label="Last salary / year (optional)" size="small" fullWidth value={row.lastSalary}
                                                        onChange={(e) => setEmpValue(i, 'lastSalary', e.target.value.replace(/\D/g, ''))}
                                                        slotProps={{ inputLabel: { shrink: true }, input: { startAdornment: <InputAdornment position="start">₹</InputAdornment> }, htmlInput: { inputMode: 'numeric' } }} sx={empField} />
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <TextField required={!row.current} label={row.current ? 'Reason for leaving (optional)' : 'Reason for leaving'} size="small" fullWidth value={row.reasonForLeaving} onChange={setEmpRow(i, 'reasonForLeaving')}
                                                        placeholder={row.current ? 'Not needed while still working' : ''} disabled={row.current} error={tried && empRowFilled(row) && !row.current && !row.reasonForLeaving.trim()} sx={empField} />
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    ))}
                                    </Stack>
                                )}
                                {form.employments.length > 0 && (
                                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#98A0AE', mt: 0.8 }}>
                                        Total experience: {durationText(form.employments.reduce((t, r) => t + empMonths(r), 0))}
                                    </Typography>
                                )}
                            </Grid>

                        <Grid size={12} sx={{ display: form.communications.length ? 'block' : 'none' }}>
                                {form.communications.length > 0 && (
                                <Stack spacing={2}>
                                    {form.communications.map((row, i) => (row.id != null && !row.editing ? (
                                        <Box key={row.id} sx={{ px: 2, py: 1.6, borderRadius: '9px', border: '1px solid #EEF0F6', bgcolor: '#FAFAFD' }}>
                                            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Stack direction="row" spacing={0.7} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.6 }}>
                                                        <Chip label={`Communication ${i + 1}`} size="small" sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: '#F1EEFE', color: '#7C5CFC' }} />
                                                        <Chip label={row.communicationType} size="small" sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: '#EEF2FF', color: '#4338CA' }} />
                                                        <Chip label={row.status} size="small" sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: '#F1F5F9', color: '#475569' }} />
                                                    </Stack>
                                                    {row.description && <Typography sx={{ fontSize: 12.5, color: '#475569', mt: 0.8 }}>{row.description}</Typography>}
                                                    <Typography sx={{ fontSize: 10.5, color: '#98A0AE', mt: 0.6 }}>
                                                        {row.savedDate}{row.savedFollowUp ? ` · follow up ${row.savedFollowUp}` : ''}{row.loggedBy ? ` · logged by ${row.loggedBy}` : ''}
                                                    </Typography>
                                                </Box>
                                                <Stack direction="row" spacing={0.2} sx={{ flexShrink: 0 }}>
                                                    <Tooltip arrow title="Edit">
                                                        <IconButton size="small" onClick={() => editCommRow(i)} sx={{ color: '#94A3B8', '&:hover': { color: '#7C5CFC', bgcolor: '#F1EEFE' } }}>
                                                            <EditRoundedIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip arrow title="Remove">
                                                        <IconButton size="small" onClick={() => removeCommRow(i)} sx={{ color: '#94A3B8', '&:hover': { color: '#E11D48', bgcolor: '#FEF2F2' } }}>
                                                            <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </Stack>
                                        </Box>
                                    ) : (
                                        <Box key={i} sx={{ px: 2, py: 2.4, borderRadius: '9px', border: '1px solid #EEF0F6', bgcolor: '#FAFAFD' }}>
                                            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                                <Chip label={`Communication ${i + 1}`} size="small" sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: '#F1EEFE', color: '#7C5CFC' }} />
                                                <IconButton size="small" onClick={() => removeCommRow(i)} sx={{ color: '#94A3B8', '&:hover': { color: '#E11D48', bgcolor: '#FEF2F2' } }}>
                                                    <CloseRoundedIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Stack>
                                            <Grid container columnSpacing={2} rowSpacing={2.5}>
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <TextField
                                                        select required label="Type" size="small" fullWidth value={row.communicationType}
                                                        onChange={setCommRow(i, 'communicationType')}
                                                        error={tried && commRowFilled(row) && !row.communicationType} sx={field}
                                                    >
                                                        {commMeta.types.map((t) => <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>{t}</MenuItem>)}
                                                    </TextField>
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <TextField
                                                        select required label="Status" size="small" fullWidth value={row.status}
                                                        onChange={setCommRow(i, 'status')}
                                                        error={tried && commRowFilled(row) && !row.status} sx={field}
                                                    >
                                                        {commMeta.statuses.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>{s}</MenuItem>)}
                                                    </TextField>
                                                </Grid>
                                                <Grid size={12}>
                                                    <TextField
                                                        label="Description" size="small" fullWidth multiline minRows={2}
                                                        value={row.description} onChange={setCommRow(i, 'description')} sx={field}
                                                    />
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: row.status === FOLLOW_UP_STATUS ? 6 : 12 }}>
                                                    <TextField
                                                        label="Communicated on (optional)" type="date" size="small" fullWidth
                                                        value={row.communicatedOn} onChange={setCommRow(i, 'communicatedOn')}
                                                        sx={empField} slotProps={{ inputLabel: { shrink: true } }}
                                                    />
                                                </Grid>
                                                {row.status === FOLLOW_UP_STATUS && (
                                                    <Grid size={{ xs: 12, sm: 6 }}>
                                                        <TextField
                                                            label="Follow-up date" type="date" size="small" fullWidth
                                                            value={row.followUpDate} onChange={setCommRow(i, 'followUpDate')}
                                                            error={tried && !row.followUpDate} sx={empField}
                                                            slotProps={{ inputLabel: { shrink: true } }}
                                                        />
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Box>
                                    )))}
                                </Stack>
                                )}
                            </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={closeDialog} disabled={saving} sx={{ ...ghostBtn, height: 40, px: 2 }}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null} sx={{ ...solidBtn, height: 40, px: 2.4 }}>
                        {saving ? 'Saving…' : dialog?.mode === 'edit' ? 'Save Changes' : 'Add Candidate'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Menu anchorEl={stageAnchor} open={Boolean(stageAnchor)} onClose={() => { setStageAnchor(null); setStageTarget(null); }}
                slotProps={{ paper: { sx: { borderRadius: '9px', minWidth: 170 } } }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#9CA3AF', px: 1.5, py: 0.7, letterSpacing: 0.4 }}>MOVE TO STAGE</Typography>
                {['applied', 'rejected'].map((key) => {
                    const st = STAGES[key];
                    return (
                        <MenuItem key={key} onClick={() => pickStage(key)} disabled={Boolean(busyId)} sx={{ fontSize: 13, gap: 1 }}>
                            <ListItemIcon sx={{ minWidth: 0 }}><Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: st.color }} /></ListItemIcon>
                            {st.label}
                            {stageTarget?.stage === key && <CheckRoundedIcon sx={{ fontSize: 16, color: st.color, ml: 'auto' }} />}
                        </MenuItem>
                    );
                })}
            </Menu>

            <Dialog open={Boolean(reject)} onClose={() => { if (!busyId) setReject(null); }} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                <DialogTitle sx={{ pb: 0.5 }}>
                    <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#0F172A' }}>Reject candidate</Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.2 }}>{reject?.name}{reject?.vacancyTitle ? ` · ${reject.vacancyTitle}` : ''}</Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    <TextField label="Reason" size="small" fullWidth multiline minRows={3} autoFocus
                        placeholder="e.g. Salary expectations above band; strong profile — keep for future roles."
                        value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} sx={field} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setReject(null)} disabled={Boolean(busyId)} sx={{ ...ghostBtn, height: 40, px: 2 }}>Cancel</Button>
                    <Button onClick={() => setStage(reject, 'Rejected', rejectReason)} disabled={Boolean(busyId) || !rejectReason.trim()}
                        startIcon={busyId ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : null}
                        sx={{ height: 40, px: 2.4, fontWeight: 700, textTransform: 'none', borderRadius: '7px', bgcolor: '#DC2626', color: '#fff', '&:hover': { bgcolor: '#B91C1C' }, '&.Mui-disabled': { bgcolor: '#FCA5A5', color: '#fff' } }}>
                        {busyId ? 'Rejecting…' : 'Reject'}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={Boolean(confirm)}
                onClose={() => { if (!deleting) setConfirm(null); }}
                onConfirm={doDelete}
                title="Remove this candidate?"
                body={confirm ? `${confirm.name} and their interview history for ${confirm.vacancyTitle || 'this vacancy'} will be removed. This cannot be undone.` : ''}
                confirmLabel="Remove"
            />

            <CommunicationLogDialog
                candidate={logFor}
                types={commMeta.types}
                statuses={commMeta.statuses}
                onClose={() => setLogFor(null)}
                notify={notify}
                onChanged={loadCandidates}
            />

            <Snackbar open={Boolean(snack)} autoHideDuration={3400} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack(null)} severity={snack?.sev || 'success'} variant="filled" sx={{ borderRadius: '7px' }}>{snack?.msg}</Alert>
            </Snackbar>
        </Box>
    );
}

// A per-candidate log, opened from the table row: view every past entry and
// add/edit/delete one at a time, independent of the candidate's own save.
function CommunicationLogDialog({ candidate, types, statuses, onClose, notify, onChanged }) {
    const [form, setForm] = useState(null); // row being added/edited, or null when just browsing
    const [editingId, setEditingId] = useState(null);
    const [tried, setTried] = useState(false);
    const [saving, setSaving] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [fetched, setFetched] = useState(null); // { id, rows } from GetCandidateCommunications
    const [loadingLog, setLoadingLog] = useState(false);
    const [reloadTick, setReloadTick] = useState(0);
    const candidateId = candidate?.id;

    // Fetch the single-candidate log whenever the dialog opens (or after a save/delete).
    useEffect(() => {
        if (candidateId == null) { setFetched(null); return undefined; }
        let cancelled = false;
        setLoadingLog(true);
        fetchCandidateRows(GetCandidateCommunications, candidateId, 'communications')
            .then((rows) => { if (!cancelled) setFetched({ id: candidateId, rows: rows.map(normalizeCommunication) }); })
            .catch(() => { if (!cancelled) setFetched(null); }) // fall back to the inline rows
            .finally(() => { if (!cancelled) setLoadingLog(false); });
        return () => { cancelled = true; };
    }, [candidateId, reloadTick]);

    const close =() => { if (!saving && !busyId) { setForm(null); setEditingId(null); onClose(); } };

    const startAdd = () => { setForm(emptyCommRow()); setEditingId(null); setTried(false); };
    const startEdit = (entry) => {
        setForm({
            communicationType: entry.communicationType || '',
            status: entry.status || '',
            description: entry.description || '',
            communicatedOn: '',
            followUpDate: prettyToISO(entry.followUpDate),
        });
        setEditingId(entry.id);
        setTried(false);
    };
    const cancelForm = () => { setForm(null); setEditingId(null); setTried(false); };

    const saveForm = async () => {
        setTried(true);
        if (!commRowValid(form)) return;
        setSaving(true);
        try {
            const { data: body } = editingId
                ? await http.put(UpdateCandidateCommunication, { id: editingId, ...commPayload(form) })
                : await http.post(AddCandidateCommunication, { candidateId: candidate.id, ...commPayload(form) });
            if (body?.error) throw new Error(body.message || 'Could not save the communication.');
            notify(editingId ? 'Communication updated.' : 'Communication logged.');
            setForm(null);
            setEditingId(null);
            setReloadTick((t) => t + 1);
            await onChanged();
        } catch (err) {
            notify(errText(err, 'Could not save the communication.'), 'error');
        } finally {
            setSaving(false);
        }
    };

    const doDelete = async () => {
        if (!deleteTarget) return;
        setBusyId(deleteTarget.id);
        try {
            const { data: body } = await http.delete(DeleteCandidateCommunication, { params: { id: deleteTarget.id } });
            if (body?.error) throw new Error(body.message || 'Could not remove the communication.');
            notify('Communication removed.');
            setDeleteTarget(null);
            setReloadTick((t) => t + 1);
            await onChanged();
        } catch (err) {
            notify(errText(err, 'Could not remove the communication.'), 'error');
        } finally {
            setBusyId(null);
        }
    };

    if (!candidate) return null;
    const list = fetched && fetched.id === candidate.id ? fetched.rows : (candidate.communications || []);

    return (
        <Dialog open={Boolean(candidate)} onClose={close} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
            <DialogTitle sx={{ pb: 1 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A' }}>Communication Log</Typography>
                <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.2 }}>{candidate.name}{candidate.vacancyTitle ? ` · ${candidate.vacancyTitle}` : ''}</Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
                {form ? (
                    <Box sx={{ p: 1.4, borderRadius: '9px', border: '1px solid #EEF0F6', bgcolor: '#FAFAFD', mb: 2 }}>
                        <Grid container spacing={1.2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField select label="Type" size="small" fullWidth value={form.communicationType}
                                    onChange={(e) => setForm((f) => ({ ...f, communicationType: e.target.value }))}
                                    error={tried && !form.communicationType} sx={field}>
                                    {types.map((t) => <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>{t}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField select label="Status" size="small" fullWidth value={form.status}
                                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                                    error={tried && !form.status} sx={field}>
                                    {statuses.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>{s}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid size={12}>
                                <TextField label="Description" size="small" fullWidth multiline minRows={2}
                                    value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} sx={field} />
                            </Grid>
                            {!editingId && (
                                <Grid size={{ xs: 12, sm: form.status === FOLLOW_UP_STATUS ? 6 : 12 }}>
                                    <TextField label="Communicated on (optional)" type="date" size="small" fullWidth
                                        value={form.communicatedOn} onChange={(e) => setForm((f) => ({ ...f, communicatedOn: e.target.value }))}
                                        sx={field} slotProps={{ inputLabel: { shrink: true } }} />
                                </Grid>
                            )}
                            {form.status === FOLLOW_UP_STATUS && (
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField label="Follow-up date" type="date" size="small" fullWidth
                                        value={form.followUpDate} onChange={(e) => setForm((f) => ({ ...f, followUpDate: e.target.value }))}
                                        error={tried && !form.followUpDate} sx={field} slotProps={{ inputLabel: { shrink: true } }} />
                                </Grid>
                            )}
                        </Grid>
                        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', mt: 1.4 }}>
                            <Button onClick={cancelForm} disabled={saving} sx={{ ...ghostBtn, height: 34, fontSize: 12.5 }}>Cancel</Button>
                            <Button onClick={saveForm} disabled={saving} startIcon={saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null} sx={{ ...solidBtn, height: 34, fontSize: 12.5 }}>
                                {saving ? 'Saving…' : editingId ? 'Save entry' : 'Log entry'}
                            </Button>
                        </Stack>
                    </Box>
                ) : (
                    <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                            <Chip label={`${list.length}/${MAX_COMM_ROWS} communications`} size="small" sx={{ height: 24, fontSize: 11, fontWeight: 700, bgcolor: '#F1EEFE', color: '#7C5CFC' }} />
                            {loadingLog && <CircularProgress size={14} sx={{ color: '#7C5CFC' }} />}
                        </Stack>
                        <Button startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />} onClick={startAdd} disabled={list.length >= MAX_COMM_ROWS} sx={{ ...tonalBtn, height: 36, fontSize: 12.5 }}>
                            Add Communication
                        </Button>
                    </Stack>
                )}

                {list.length === 0 ? (
                    <EmptyState icon={ChatBubbleOutlineRoundedIcon} title="No communications logged yet" hint="Calls, emails, WhatsApp and meetings you log will show up here." />
                ) : (
                    <Stack spacing={1}>
                        {list.map((entry) => (
                            <Box key={entry.id} sx={{ p: 1.3, borderRadius: '9px', border: '1px solid #F1F0F9', opacity: busyId === entry.id ? 0.5 : 1 }}>
                                <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                    <Stack direction="row" spacing={0.7} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                                        <Chip label={entry.communicationType} size="small" sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: '#EEF2FF', color: '#4338CA' }} />
                                        <Chip label={entry.status} size="small" sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: '#F1F5F9', color: '#475569' }} />
                                    </Stack>
                                    <Stack direction="row" spacing={0.2}>
                                        <IconButton size="small" disabled={Boolean(busyId)} onClick={() => startEdit(entry)} sx={{ color: '#94A3B8', '&:hover': { color: '#7C5CFC', bgcolor: '#F1EEFE' } }}>
                                            <EditRoundedIcon sx={{ fontSize: 15 }} />
                                        </IconButton>
                                        <IconButton size="small" disabled={Boolean(busyId)} onClick={() => setDeleteTarget(entry)} sx={{ color: '#94A3B8', '&:hover': { color: '#E11D48', bgcolor: '#FEF2F2' } }}>
                                            <DeleteOutlineRoundedIcon sx={{ fontSize: 15 }} />
                                        </IconButton>
                                    </Stack>
                                </Stack>
                                {entry.description && <Typography sx={{ fontSize: 12.5, color: '#475569', mt: 0.6 }}>{entry.description}</Typography>}
                                <Typography sx={{ fontSize: 10.5, color: '#98A0AE', mt: 0.6 }}>
                                    {entry.communicatedOn}{entry.followUpDate ? ` · follow up ${entry.followUpDate}` : ''}{entry.loggedBy ? ` · logged by ${entry.loggedBy}` : ''}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={close} sx={{ ...ghostBtn, height: 38, px: 2 }}>Close</Button>
            </DialogActions>

            <ConfirmDialog
                open={Boolean(deleteTarget)}
                onClose={() => { if (!busyId) setDeleteTarget(null); }}
                onConfirm={doDelete}
                title="Remove this entry?"
                body="This removes it from the candidate's communication log."
                confirmLabel="Remove"
            />
        </Dialog>
    );
}
